import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, UserPlus, UserCheck, Flag } from "lucide-react";
import { toast } from "sonner";
import { useAllShows } from "../hooks/useShows";
import LogCard from "../components/LogCard";
import ReportModal from "../components/ReportModal";
import FollowList from "../components/FollowList";
import { TOAST_MS } from "../lib/constants";
import {
  getUserByUsername, getFollowStatus,
  followUser, unfollowUser,
  getUserLogs,
} from "../lib/services";
import { formatApiError } from "../lib/api";
import { lower } from "../lib/strings";

export default function User() {
  const { username } = useParams();
  const navigate = useNavigate();
  const allShows = useAllShows();
  const findShow = (id) => allShows.find((s) => s.id === id);

  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [followListMode, setFollowListMode] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [u, s, l] = await Promise.all([
        getUserByUsername(username),
        getFollowStatus(username),
        getUserLogs(username),
      ]);
      setUser(u);
      setStatus(s);
      setLogs(l.items || []);
      setError(null);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoaded(true);
    }
  }, [username]);

  useEffect(() => { refresh(); }, [refresh]);

  const onLogDeleted = (deletedId) => {
    setLogs((arr) => arr.filter((it) => (it.log?.id || it.id) !== deletedId));
  };

  const onFollowToggle = async () => {
    if (!status) return;
    try {
      if (status.isFollowing) {
        await unfollowUser(username);
        toast("unfollowed", { duration: TOAST_MS.FAST });
      } else {
        await followUser(username);
        toast("followed", { duration: TOAST_MS.FAST });
      }
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  if (error) {
    return (
      <main className="min-h-screen bg-black text-white p-8 pb-28">
        <div className="font-display text-3xl">user not found.</div>
        <Link to="/home" className="mt-4 inline-block font-mono text-sm text-acid underline">
          back home
        </Link>
      </main>
    );
  }

  if (!loaded || !user) {
    return (
      <main className="min-h-screen bg-black text-white p-8 pb-28">
        <div className="font-mono text-xs text-white/40">loading...</div>
      </main>
    );
  }

  return (
    <main data-testid={`user-page-${user.id}`} className="min-h-screen bg-black text-white pb-28">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <button
          onClick={() => navigate(-1)}
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> back
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-3">
          @{user.username}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none mt-1">
          {lower(user.displayName) || lower(user.username) || "user"}<span className="text-acid">.</span>
        </h1>
        {user.bio && (
          <p className="font-mono text-[11px] text-white/60 mt-2">{user.bio}</p>
        )}

        <div
          data-testid="user-social-counts"
          className="mt-3 flex items-center gap-4 font-mono text-[11px] text-white/60"
        >
          <button
            type="button"
            data-testid="user-following-tap"
            onClick={() => setFollowListMode("following")}
            className="hover:text-acid transition-colors"
          >
            <span data-testid="user-following-count" className="text-white tabular-nums">
              {status?.followingCount ?? 0}
            </span>{" "}
            following
          </button>
          <span className="text-white/30">·</span>
          <button
            type="button"
            data-testid="user-followers-tap"
            onClick={() => setFollowListMode("followers")}
            className="hover:text-acid transition-colors"
          >
            <span data-testid="user-followers-count" className="text-white tabular-nums">
              {status?.followerCount ?? 0}
            </span>{" "}
            followers
          </button>
        </div>

        {!status?.isSelf && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              data-testid="follow-toggle"
              onClick={onFollowToggle}
              className={`font-mono text-xs lowercase border-2 px-3 py-2 transition-colors flex items-center gap-1.5 ${
                status?.isFollowing
                  ? "border-acid bg-acid text-black"
                  : "border-acid text-acid hover:bg-acid hover:text-black"
              }`}
            >
              {status?.isFollowing ? (
                <UserCheck className="w-3.5 h-3.5" strokeWidth={2} />
              ) : (
                <UserPlus className="w-3.5 h-3.5" strokeWidth={2} />
              )}
              {status?.isFollowing ? "following" : "follow"}
            </button>
            <button
              data-testid="report-user"
              onClick={() => setReporting(true)}
              className="font-mono text-xs lowercase border-2 border-white/30 text-white/60 px-3 py-2 hover:border-white hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Flag className="w-3.5 h-3.5" strokeWidth={2} />
              report
            </button>
          </div>
        )}
      </header>

      <ReportModal
        open={reporting}
        onClose={() => setReporting(false)}
        targetType="user"
        targetId={user?.id}
        label={user?.username ? `@${user.username}` : "user"}
      />

      <FollowList
        open={!!followListMode}
        onClose={() => setFollowListMode(null)}
        username={user.username}
        mode={followListMode || "followers"}
      />

      <section className="px-5 py-6">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
          recent diary ({logs.length})
        </h2>

        {logs.length === 0 ? (
          <div className="border border-white/15 p-5 text-center">
            <p className="font-mono text-xs text-white/40">no public logs yet.</p>
          </div>
        ) : (
          <ul className="space-y-5">
            {logs.map((item) => {
              // /users/:username/logs returns enriched envelopes.
              const log = item.log || item;
              const show = item.show || findShow(log.showId);
              const itemUser = item.user || user;
              return (
                <li key={log.id || `${log.showId}-${log.createdAt || ""}`} className="border-b border-white/10 pb-5">
                  <LogCard log={log} show={show} user={itemUser} showUser={false} onDeleted={onLogDeleted} />
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
